import './RecentActivityContainer.css';

import { Roles } from '@dataquiver/stoneage-roles';
import { IonCol, IonGrid, IonRow } from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { selectAuthUserInfo } from 'src/redux/auth/selectors';
import { useSocket } from 'src/utils/socket';
import { GenericMessages } from '../../intl/GenericMessages';
import { HomeMessages } from '../../intl/HomeMessages';
import { JobMessages } from '../../intl/JobMessages';
import { AppRouteConstants } from '../../pages/app-layout/AppRouteConstants';
import { SocketContextPortal } from '../../utils/socket/context/portalSocketContext';
import { SocketContextSentinel } from '../../utils/socket/context/sentinelSocketContext';
import { LastJobCard } from './cards/LastJobCard';
import { RecentActivityDetailCard } from './cards/RecentActivityDetailCard';

export const data = [
  ['Type', 'Duration'],
  ['Positioning', 5],
  ['Lance Feeding', 11],
  ['Controller Off', 3],
  ['Idle', 2],
];

const RecentActivityContainer: React.FC = () => {
  const intl = useIntl();
  const history = useHistory();

  const userData = useSelector(selectAuthUserInfo);
  const userOrg = userData?.orgDetails;

  // Log counts
  const [jobCount, setJobCount] = useState(0);
  const [jobsFetched, setJobsFetched] = useState(false);
  const { request: sentinelRequest, connected } = useSocket(SocketContextSentinel, undefined);

  // Live Log Counts
  const [liveJobCount, setLiveJobCount] = useState(0);
  const [liveJobFetched, setLiveJobsFetched] = useState(false);

  // User Counts
  const [userCount, setUserCount] = useState(0);
  const [usersFetched, setUsersFetched] = useState(false);
  const { request: portalRequest, connected: portalConnected } = useSocket(SocketContextPortal, undefined);

  useEffect(() => {
    if (userOrg && connected) {
      sentinelRequest('logs searchCount', {})
        .then((result: any) => {
          setJobCount(result);
        })
        .finally(() => {
          setJobsFetched(true);
        });

      sentinelRequest('logs searchCount', { statuses: ['in-progress'] })
        .then((result: any) => {
          setLiveJobCount(result);
        })
        .finally(() => {
          setLiveJobsFetched(true);
        });
    }
  }, [sentinelRequest, connected, userOrg]);

  useEffect(() => {
    if (Roles.isManager(userData?.role) && userOrg && portalConnected) {
      portalRequest('users count', { query: { org: userOrg._id } })
        .then((result: any) => {
          setUserCount(result);
        })
        .finally(() => {
          setUsersFetched(true);
        });
    }
  }, [portalRequest, userOrg, portalConnected, userData?.role, sentinelRequest]);

  const handleCleanConnectNav = () => {
    const optimizelyUrl = process.env.REACT_APP_OPTIMIZELY_UI_URL;
    window.open(!!userOrg ? `${optimizelyUrl}/MyAccount/UserAdministration` : optimizelyUrl, '_blank');
  };

  const onNavigate = (route?: string) => {
    if (!route) {
      return;
    }
    history.push(route);
  };

  return (
    <IonGrid className="recent-activity-container">
      {!!userData && !!userData.org && (
        <>
          <IonRow>
            <IonCol size="12">
              <LastJobCard userData={userData} userOrgType={userOrg?.type} />
            </IonCol>
          </IonRow>
          <IonRow>
            {Roles.isManager(userData.role) && (
              <IonCol size="12" sizeMd="4">
                <RecentActivityDetailCard
                  loading={!usersFetched}
                  body={userCount.toString()}
                  title={userOrg?.orgName ?? intl.formatMessage(GenericMessages.unknownPlaceholder)}
                  footer={intl.formatMessage(HomeMessages.homeActiveUsersCount)}
                  onClick={handleCleanConnectNav}
                  automationId="activeUsers"
                />
              </IonCol>
            )}
            <IonCol size="12" sizeMd="4">
              <RecentActivityDetailCard
                loading={!jobsFetched}
                body={jobCount.toString()}
                title={intl.formatMessage(JobMessages.jobLogsTitle)}
                footer={
                  userOrg?.type === 'asset owner'
                    ? intl.formatMessage(JobMessages.jobCountSharedJobLogs)
                    : intl.formatMessage(JobMessages.jobCountUploadedJobLogs)
                }
                linkRoute={AppRouteConstants.LogsList}
                onClick={onNavigate}
                automationId="uploadedJobLogs"
              />
            </IonCol>

            <IonCol size="12" sizeMd="4">
              <RecentActivityDetailCard
                loading={!liveJobFetched}
                body={liveJobCount.toString()}
                title={intl.formatMessage(JobMessages.liveJobsTitle)}
                footer={intl.formatMessage(JobMessages.jobInProgressJobLogs)}
                linkRoute={AppRouteConstants.LogsList}
                onClick={onNavigate}
                automationId="liveJobs"
              />
            </IonCol>
          </IonRow>
        </>
      )}
    </IonGrid>
  );
};

export default RecentActivityContainer;
